import { useState, useEffect } from "react";
import { useParams, useNavigate, Link } from "react-router-dom";
import api from "@/services/api";

export default function PaymentPage() {
  const { token } = useParams<{ token: string }>();
  const navigate = useNavigate();

  const [order, setOrder] = useState<any>(null);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState("");
  const [alreadyPaid, setAlreadyPaid] = useState(false);
  const [showIframe, setShowIframe] = useState(false);

  const TERMINAL = "s1410596";
  const BASE_URL = window.location.origin;

  useEffect(() => {
    if (!token) {
      navigate("/");
      return;
    }

    api
      .get(`/payment-orders/${token}`)
      .then((res) => {
        if (res.data.paid) {
          setAlreadyPaid(true);
          setLoading(false);
          return;
        }

        if (res.data.error) {
          setError(res.data.error);
          setLoading(false);
          return;
        }

        setOrder(res.data);
        setShowIframe(true);
        setLoading(false);
      })
      .catch(() => {
        setError("الرابط غير صالح أو منتهي الصلاحية");
        setLoading(false);
      });
  }, [token, navigate]);

  useEffect(() => {
    const handler = (e: MessageEvent) => {
      if (!e.origin.includes("tranzila")) {
        return;
      }

      try {
        const data = typeof e.data === "string" ? JSON.parse(e.data) : e.data;

        if (data?.Response === "000") {
          navigate(`/payment-success?order_id=${order?.order_number || ""}`);
        } else if (data?.Response) {
          navigate(
            `/payment-fail?order_id=${
              order?.order_number || ""
            }&token=${token || ""}`,
          );
        }
      } catch {
        // تجاهل الرسائل غير الصالحة
      }
    };

    window.addEventListener("message", handler);

    return () => {
      window.removeEventListener("message", handler);
    };
  }, [navigate, order?.order_number, token]);

  const getIframeUrl = () => {
    if (!order) {
      return "";
    }

    const addr = order.address || {};
    const usr = order.user || {};

    const productNames = (order.order_items || [])
      .slice(0, 3)
      .map((item: any) => item.product_name || "")
      .filter(Boolean)
      .join(", ");

    const paymentAmount =
      Number(order.remaining_amount) > 0
        ? order.remaining_amount
        : order.total_cost;

    const params = new URLSearchParams({
      sum: String(paymentAmount),
      currency: "1",
      lang: "ar",

      // UI
      nologo: "1",

      // Payment methods
      bit_pay: "1",
      google_pay: "1",
      apple_pay: "1",

      // Payment Request
      allowpaymentrequest: "true",

      // Colors
      trBgColor: "F5EDE7",
      trTextColor: "1A1A1A",
      trButtonColor: "1A1A1A",

      // Button
      buttonLabel: "ادفع الآن",

      // Customer
      contact:
        `${addr.first_name || ""} ${addr.last_name || ""}`.trim() ||
        usr.name ||
        "",

      email: usr.email || "",

      phone: addr.phone || usr.phone || "",

      address: addr.address || "",

      city: addr.city || "",

      // Order
      remarks: order.order_number || "",

      pdesc: productNames || "",

      // Redirects
      success_url_address: `${BASE_URL}/payment-success?order_id=${encodeURIComponent(
        order.order_number,
      )}`,

      fail_url_address: `${BASE_URL}/payment-fail?order_id=${encodeURIComponent(
        order.order_number,
      )}&token=${encodeURIComponent(token || "")}`,

      // Webhook
      notify_url_address: `${BASE_URL}/api/payments/webhook`,

      // Disable DC
      DCdisable: order.order_number,
    });

    return `https://directng.tranzila.com/${TERMINAL}/iframenew.php?${params.toString()}`;
  };

  /*
   |--------------------------------------------------------------------------
   | Loading
   |--------------------------------------------------------------------------
   */

  if (loading) {
    return (
      <main className="min-h-screen flex items-center justify-center bg-cream-50">
        <div className="text-center">
          <div className="animate-pulse w-12 h-12 bg-cream-200 rounded-full mx-auto mb-4" />

          <p className="text-charcoal-400 text-sm">جاري التحميل...</p>
        </div>
      </main>
    );
  }

  /*
   |--------------------------------------------------------------------------
   | Already Paid
   |--------------------------------------------------------------------------
   */

  if (alreadyPaid) {
    return (
      <main className="min-h-screen flex items-center justify-center bg-cream-50 py-16">
        <div className="text-center max-w-md mx-auto px-4">
          <div className="w-20 h-20 rounded-full bg-green-50 flex items-center justify-center mx-auto mb-6">
            <i className="ri-check-line text-3xl text-green-600" />
          </div>

          <h1 className="text-2xl font-bold text-charcoal-900 mb-3">
            تم الدفع مسبقاً
          </h1>

          <p className="text-charcoal-600 mb-6 text-sm">
            تم دفع هذا الطلب مسبقاً.
          </p>

          <Link to="/shop" className="btn-primary inline-block">
            مواصلة التسوق
          </Link>
        </div>
      </main>
    );
  }

  /*
   |--------------------------------------------------------------------------
   | Error
   |--------------------------------------------------------------------------
   */

  if (error) {
    return (
      <main className="min-h-screen flex items-center justify-center bg-cream-50 py-16">
        <div className="text-center max-w-md mx-auto px-4">
          <div className="w-20 h-20 rounded-full bg-red-50 flex items-center justify-center mx-auto mb-6">
            <i className="ri-close-line text-3xl text-red-600" />
          </div>

          <h1 className="text-2xl font-bold text-charcoal-900 mb-3">
            رابط غير صالح
          </h1>

          <p className="text-charcoal-600 mb-6 text-sm">{error}</p>

          <Link to="/shop" className="btn-primary inline-block">
            مواصلة التسوق
          </Link>
        </div>
      </main>
    );
  }

  /*
   |--------------------------------------------------------------------------
   | Payment
   |--------------------------------------------------------------------------
   */

  const paymentAmount =
    Number(order?.remaining_amount) > 0
      ? order.remaining_amount
      : order?.total_cost;

  return (
    <main className="min-h-screen bg-cream-50">
      {/* Header */}
      <div className="bg-cream-200 py-8 md:py-12">
        <div className="container-main text-center">
          <h1 className="heading-xl mb-3">إتمام الدفع</h1>

          <p className="body-text">
            الطلب {order?.order_number} - المبلغ: {paymentAmount} شيكل
          </p>
        </div>
      </div>

      {/* Payment */}
      <div className="container-main py-8">
        <div className="max-w-2xl mx-auto">
          {/* Order info */}
          <div className="bg-white rounded-2xl border border-cream-300 shadow-sm p-4 mb-4 flex items-center justify-between">
            <div>
              <span className="text-sm text-charcoal-500">رقم الطلب</span>

              <p className="font-mono font-bold text-charcoal-900">
                {order?.order_number}
              </p>
            </div>

            <div>
              <span className="text-sm text-charcoal-500">المبلغ</span>

              <p className="text-xl font-bold text-charcoal-900">
                {paymentAmount} ₪
              </p>
            </div>
          </div>

          {/* Tranzila */}
          {showIframe && (
            <div className="bg-[#F5EDE7] rounded-2xl border border-cream-300 shadow-sm overflow-hidden p-4 md:p-6">
              <iframe
                src={getIframeUrl()}
                title="Tranzila Payment"
                className="w-full border-0 rounded-xl"
                style={{
                  height: "700px",
                  background: "#F5EDE7",
                }}
                allow="payment *"
                scrolling="no"
              />
            </div>
          )}
        </div>
      </div>
    </main>
  );
}
